import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRange } from 'react-date-range';
import moment from 'moment';
import Layouts from '../../layouts/Layouts';
import { getAuthorizationHeader, http } from '../../config/http';
import { IconCalendar, IconReload, IconInbox, IconSend } from '@tabler/icons-react';
import { DinamicTitle } from '../../utils/DinamicTitle';
import 'react-date-range/dist/styles.css'; 
import 'react-date-range/dist/theme/default.css';

const BranchDashboard = ({ cabangId, cabangName, tglAwal, tglAkhir }) => {
  const [loading, setLoading] = useState(false);
  const [summaryMasuk, setSummaryMasuk] = useState({ totalBarang: 0, totalBerat: 0, categories: {} });
  const [summaryKeluar, setSummaryKeluar] = useState({ totalBarang: 0, totalBerat: 0, categories: {} });
  
  useEffect(() => {
    async function fetchData() {
      const userRole = localStorage.getItem('role');
      const isOperator = userRole === 'operator_rumah' || userRole === 'kasir' || userRole === 'operator';
      
      if (!cabangId && !isOperator) return;
      setLoading(true);
      
      try {
        const cabangParamMasuk = isOperator ? '' : `&cabang=${cabangId}`;
        const cabangParamKeluar = isOperator ? '' : `&cabang_id=${cabangId}`;
        
        const [resMasuk, resKeluar] = await Promise.all([
          http.get(`/item/brankas?page=1&per_page=5000&tgl_awal=${tglAwal}&tgl_akhir=${tglAkhir}&status_pengiriman=ready${cabangParamMasuk}`, { headers: { Authorization: getAuthorizationHeader() }, timeout: 15000 }).catch(e => { console.error('Masuk Error:', e); return { data: { data: { data: [] } } }; }),
          http.get(`/permohonan-brankas/histori-pindah?page=1&per_page=5000&start_date=${tglAwal}&end_date=${tglAkhir}${cabangParamKeluar}`, { headers: { Authorization: getAuthorizationHeader() }, timeout: 15000 }).catch(e => { console.error('Keluar Error:', e); return { data: { data: { data: [] } } }; })
        ]);

        const dataMasuk = resMasuk.data?.data?.data || [];
        const historiPindah = resKeluar.data?.data?.data || [];
        
        const dataKeluar = historiPindah
          .filter(h => h.status_item?.toLowerCase() === 'disetujui')
          .map(h => h.item)
          .filter(Boolean);

        // Reduce Masuk
        const sumMasuk = dataMasuk.reduce((acc, item) => {
          acc.totalBarang += 1;
          acc.totalBerat += parseFloat(item.berat || 0);
          const cat = item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori';
          if (!acc.categories[cat]) acc.categories[cat] = { pcs: 0, berat: 0 };
          acc.categories[cat].pcs += 1;
          acc.categories[cat].berat += parseFloat(item.berat || 0);
          return acc;
        }, { totalBarang: 0, totalBerat: 0, categories: {} });

        setSummaryMasuk(sumMasuk);

        // Reduce Keluar
        const sumKeluar = dataKeluar.reduce((acc, item) => {
          acc.totalBarang += 1;
          acc.totalBerat += parseFloat(item.berat || 0);
          const cat = item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori';
          if (!acc.categories[cat]) acc.categories[cat] = { pcs: 0, berat: 0 };
          acc.categories[cat].pcs += 1;
          acc.categories[cat].berat += parseFloat(item.berat || 0);
          return acc;
        }, { totalBarang: 0, totalBerat: 0, categories: {} });

        setSummaryKeluar(sumKeluar);
      } catch (err) {
        console.error("Error fetching branch dashboard", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [cabangId, tglAwal, tglAkhir]);

  const RenderSummaryCard = ({ title, summary, type }) => {
    const isMasuk = type === 'masuk';
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
        <div className={`p-4 border-b flex items-center gap-3 ${
          isMasuk ? 'bg-sky-50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-800' 
                 : 'bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800'
        }`}>
          <div className={`p-2 rounded-lg ${isMasuk ? 'bg-sky-500 text-white' : 'bg-rose-500 text-white'}`}>
            {isMasuk ? <IconInbox size={24} /> : <IconSend size={24} />}
          </div>
          <div>
            <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg">{title}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">Rangkuman berdasarkan filter tanggal aktif</p>
          </div>
        </div>
        
        <div className="p-5 flex-1">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700/30 p-3.5 rounded-xl border border-slate-100 dark:border-slate-700">
              <span className="text-xs font-semibold text-slate-500 block mb-1">Total Item:</span>
              <span className="text-2xl font-extrabold text-slate-800 dark:text-slate-100">{summary.totalBarang} <span className="text-sm font-normal">PCS</span></span>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700/30 p-3.5 rounded-xl border border-slate-100 dark:border-slate-700">
              <span className="text-xs font-semibold text-slate-500 block mb-1">Total Berat:</span>
              <span className={`text-2xl font-extrabold ${isMasuk ? 'text-sky-600 dark:text-sky-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {summary.totalBerat.toFixed(3)} <span className="text-sm font-normal">gr</span>
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Rincian per Kategori:</h4>
            {Object.keys(summary.categories).length === 0 ? (
              <div className="text-center p-6 text-sm text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-dashed border-slate-200 dark:border-slate-700">
                Tidak ada data pada periode ini
              </div>
            ) : (
              Object.entries(summary.categories).map(([catName, catData], idx) => (
                <div key={idx} className="flex justify-between items-center text-sm bg-white dark:bg-slate-800 px-3 py-2.5 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                  <span className="font-semibold text-slate-700 dark:text-slate-300">{catName}</span>
                  <span className="font-bold text-slate-600 dark:text-slate-400">
                    {catData.pcs} PCS <span className="font-normal opacity-70">({catData.berat.toFixed(3)}gr)</span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-10">
      {cabangName && (
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <div className="w-2 h-6 bg-amber-500 rounded-full"></div>
          {cabangName}
        </h2>
      )}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-10 text-center space-y-4">
           <div className="w-10 h-10 rounded-full animate-spin border-4 border-dashed border-amber-500 border-t-transparent"></div>
           <p className="text-slate-500 text-sm font-medium animate-pulse">Memuat Data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <RenderSummaryCard title="Barang Masuk" summary={summaryMasuk} type="masuk" />
          <RenderSummaryCard title="Barang Keluar (Pusat)" summary={summaryKeluar} type="keluar" />
        </div>
      )}
    </div>
  );
};

const HistoriMasukKeluar = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem('role');
  const idBrankas = localStorage.getItem('id_brankas');

  const [selectCabang, setSelectCabang] = useState([]);
  const [loadingCabang, setLoadingCabang] = useState(true);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  DinamicTitle('Dashboard Masuk - Keluar || Komaladewi');

  useEffect(() => {
    async function getCabangSelect() {
      if (role === 'operator_rumah' || role === 'kasir' || role === 'operator') {
        setLoadingCabang(false);
        return; // Operators don't need to fetch cabang list
      }
      try {
        const res = await http.get(`cabang/brankas`, {
          headers: { Authorization: getAuthorizationHeader() },
        });
        setSelectCabang(res.data.data || []);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoadingCabang(false);
      }
    }
    getCabangSelect();
  }, [navigate, role]);

  const tglAwal = moment(startDate).format('YYYY-MM-DD');
  const tglAkhir = moment(endDate).format('YYYY-MM-DD');

  const selectionRange = {
    startDate: startDate,
    endDate: endDate,
    key: 'selection',
  };

  function handleSelect(date) {
    setStartDate(date.selection.startDate);
    setEndDate(date.selection.endDate);
  }

  return (
    <Layouts title={'Dashboard Masuk - Keluar Brankas'}>
      <div className="p-4 sm:p-6 bg-slate-50/50 dark:bg-slate-900/50 min-h-[70vh]">
        
        {/* Toolbar in Content */}
        <div className="flex items-center gap-2 mb-6 w-full flex-wrap bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm sticky top-0 z-40">
          <div className="dropdown dropdown-bottom">
            <div
              tabIndex={0}
              className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 p-2 rounded-lg cursor-pointer transition-colors shadow-sm flex items-center justify-center gap-2 px-4 text-sm font-medium border border-slate-200 dark:border-slate-600"
            >
              <IconCalendar size={18} />
              <span>{moment(startDate).format('DD MMM YYYY')} - {moment(endDate).format('DD MMM YYYY')}</span>
            </div>
            <div tabIndex={0} className="dropdown-content menu p-2 mt-2 shadow-2xl bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 z-50">
              <DateRange ranges={[selectionRange]} onChange={handleSelect} />
            </div>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 p-2 rounded-lg transition-colors border border-slate-200 dark:border-slate-600 flex items-center justify-center gap-2 px-4 text-sm font-medium"
            title="Muat Ulang"
          >
            <IconReload size={18} />
            <span className="hidden sm:inline">Muat Ulang</span>
          </button>
        </div>

        <div className="max-w-5xl mx-auto">
          {(role === 'operator_rumah' || role === 'kasir' || role === 'operator') ? (
            <BranchDashboard 
              key="operator" 
              cabangId="operator" 
              cabangName={null} 
              tglAwal={tglAwal} 
              tglAkhir={tglAkhir} 
            />
          ) : (
            <>
              {selectCabang.map((cb) => (
                <BranchDashboard 
                  key={cb.id} 
                  cabangId={cb.id} 
                  cabangName={cb.nama_cabang} 
                  tglAwal={tglAwal} 
                  tglAkhir={tglAkhir} 
                />
              ))}
              {loadingCabang ? (
                 <div className="flex flex-col items-center justify-center py-10 text-center space-y-4">
                    <div className="w-10 h-10 rounded-full animate-spin border-4 border-dashed border-amber-500 border-t-transparent"></div>
                    <p className="text-slate-500 text-sm font-medium animate-pulse">Memuat Daftar Brankas...</p>
                 </div>
              ) : (
                selectCabang.length === 0 && (
                   <div className="flex flex-col items-center justify-center py-10 text-center space-y-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
                      <IconInbox size={48} className="text-slate-300 dark:text-slate-600 mb-2" />
                      <h3 className="text-lg font-bold text-slate-700 dark:text-slate-200">Tidak Ada Brankas</h3>
                      <p className="text-slate-500 text-sm">Tidak ada daftar brankas yang tersedia untuk akun ini.</p>
                   </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </Layouts>
  );
};

export default HistoriMasukKeluar;
